import { map, uniq } from 'es-toolkit/compat';

import { FileToDownload } from 'components/atoms/DownloadFilesModal';
import { ResultsMode } from 'interfaces/experimentResults';
import { CasesParams } from 'utils/useCasesParams/types';
import { fetchFeaturesCsvBlob } from 'utils/exports';
import { downloadFilesAsZip, ZipDownloadError } from 'utils/zipDownload';

type FeaturesCsvOptions = Parameters<typeof fetchFeaturesCsvBlob>[0];

// One Features request: the selected slides of one panel that take their results from one source.
export interface FeaturesExportGroup {
  key: string;
  // Names the panel and results source in file names and error messages, for example `Immune, Published results`.
  title: string;
  // Used in the CSV's file name.
  fileStem: string;
  resultsMode: ResultsMode;
  // Set for a run, which is requested on its own.
  orchestrationId?: string;
  casesParams: Partial<CasesParams>;
}

const toFileName = (studyId: string, stem: string, suffix: string) => `features_study_${studyId}_${stem}${suffix}.csv`;

const sanitizeStem = (stem: string) => stem.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'features';

// Two groups never share a file name, so a ZIP never holds two entries of one name.
const getUniqueStems = (groups: FeaturesExportGroup[]) => {
  const used = new Map<string, number>();
  return map(groups, ({ fileStem }) => {
    const stem = sanitizeStem(fileStem);
    const count = (used.get(stem) ?? 0) + 1;
    used.set(stem, count);
    return count === 1 ? stem : `${stem}_${count}`;
  });
};

// One Features CSV per panel and results source, in one ZIP. The CSVs are never merged: each is the existing
// `features` response for its source with only that panel's selected slides (`slideIdsToInclude`). The ZIP is all or
// nothing, and a failure names the groups that failed.
export const exportFeaturesForGroups = async ({
  groups,
  ...options
}: Omit<FeaturesCsvOptions, 'orchestrationId' | 'resultsMode' | 'casesParams'> & { groups: FeaturesExportGroup[] }) => {
  const { studyId, highlightedOnly, signal } = options;
  const suffix = highlightedOnly ? '_highlighted' : '';
  const stems = getUniqueStems(groups);
  const files: FileToDownload[] = map(groups, (group, index) => ({
    id: group.key,
    // The bytes come from `prepare`; the URL only identifies the entry.
    url: `features://${studyId}/${group.key}${suffix}`,
    title: group.title,
    fileName: toFileName(studyId, stems[index], suffix),
    prepare: (prepareSignal: AbortSignal) =>
      fetchFeaturesCsvBlob({
        ...options,
        // Cluster labels are resolved for the run this CSV is requested for.
        context: { ...options.context, orchestrationId: group.orchestrationId },
        resultsMode: group.resultsMode,
        orchestrationId: group.orchestrationId,
        casesParams: group.casesParams,
        signal: prepareSignal,
      }),
  }));
  try {
    await downloadFilesAsZip({
      files,
      archiveName: `features_study_${studyId}_panels${suffix}.zip`,
      signal: signal ?? new AbortController().signal,
    });
  } catch (error) {
    if (error instanceof ZipDownloadError && error.failedFileNames.length) {
      const reasons = uniq(error.failureReasons);
      throw new Error(
        `Features could not be retrieved for ${error.failedFileNames.join(', ')}.${
          reasons.length ? ` ${reasons.join(' ')}` : ''
        }`
      );
    }
    throw error;
  }
};
